// Cache SWR-like partagé par les onglets admin.
//
// But : rendre les navigations entre onglets INSTANTANÉES. Au lieu de
// montrer "Chargement…" à chaque remount, on garde la dernière réponse
// en mémoire (TTL configurable) et on la retourne immédiatement. Un
// refetch silencieux est lancé en tâche de fond si la donnée a dépassé
// le TTL — l'UI se met à jour quand la nouvelle réponse arrive.
//
// On déduplique aussi les requêtes concurrentes pour éviter de spam le
// backend si deux composants demandent la même clé en même temps.

import { useCallback, useEffect, useRef, useState } from "react";

const _store = new Map(); // key → { data, fetchedAt, inflight }

export function peekAdminCache(key) {
  return _store.get(key)?.data;
}

export function clearAdminCache(key) {
  if (key == null) _store.clear();
  else _store.delete(key);
}

/**
 * @param {string} key — clé de cache (unique par endpoint + params)
 * @param {() => Promise<any>} fetcher — fonction qui retourne la data
 * @param {{ ttlMs?: number, pollMs?: number, enabled?: boolean, initialData?: any }} opts
 * @returns {{ data, loading, refreshing, error, reload: (force?) => Promise<void> }}
 */
export function useAdminFetch(key, fetcher, opts = {}) {
  const { ttlMs = 60_000, pollMs = 0, enabled = true, initialData = null } = opts;

  const cached = _store.get(key);
  const [data, setData] = useState(cached?.data ?? initialData);
  // "loading" uniquement si on n'a RIEN à afficher. Sinon on utilise
  // "refreshing" pour ne pas masquer l'UI pendant la revalidation.
  const [loading, setLoading] = useState(enabled && !cached?.data);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const reload = useCallback(
    async (force = false) => {
      if (!enabled) return;
      const entry = _store.get(key);
      const fresh = entry?.fetchedAt && Date.now() - entry.fetchedAt < ttlMs;
      if (!force && fresh && entry.data) {
        setData(entry.data);
        setLoading(false);
        return;
      }
      // Dédup : si une requête tourne déjà pour cette clé, on la
      // partage au lieu d'en lancer une nouvelle.
      if (entry?.inflight) {
        try {
          const d = await entry.inflight;
          setData(d);
          setError(null);
        } catch (e) {
          setError(e);
        } finally {
          setLoading(false);
          setRefreshing(false);
        }
        return;
      }
      const hasAny = !!(entry?.data || data);
      if (hasAny) setRefreshing(true);
      else setLoading(true);
      const p = Promise.resolve()
        .then(() => fetcherRef.current())
        .then((d) => {
          _store.set(key, { data: d, fetchedAt: Date.now(), inflight: null });
          return d;
        })
        .catch((e) => {
          _store.set(key, { ...(entry || {}), inflight: null });
          throw e;
        });
      _store.set(key, { ...(entry || {}), inflight: p });
      try {
        const d = await p;
        setData(d);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [key, ttlMs, enabled, data],
  );

  // Fetch initial au mount + à chaque fois que la clé change.
  useEffect(() => {
    if (!enabled) return undefined;
    const entry = _store.get(key);
    if (entry?.data) {
      setData(entry.data);
      setLoading(false);
      const stale = !entry.fetchedAt || Date.now() - entry.fetchedAt >= ttlMs;
      if (stale) reload(false);
    } else {
      reload(false);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  // Polling optionnel
  useEffect(() => {
    if (!enabled || !pollMs) return undefined;
    const t = setInterval(() => reload(true), pollMs);
    return () => clearInterval(t);
  }, [enabled, pollMs, reload]);

  return { data, loading, refreshing, error, reload };
}
